import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Package, 
  Users, 
  Percent, 
  CreditCard, 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle,
  DollarSign,
  Calendar,
  BarChart3,
  Settings,
  LogOut,
  RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  collection, 
  getDocs, 
  query,
  orderBy,
  where 
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const AdminDashboardNew = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalRevenue: 0,
    totalMenuItems: 0,
    activeDiscounts: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      console.log('🔄 Fetching dashboard data...');
      
      // Initialize default values
      let orders = [];
      let menuItems = [];
      let discounts = [];

      // Fetch orders with error handling
      try {
        const ordersQuery = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
        const ordersSnapshot = await getDocs(ordersQuery);
        orders = ordersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('📦 Fetched orders:', orders.length);
      } catch (ordersError) {
        console.warn('⚠️ Could not fetch orders:', ordersError.message);
        // Continue with empty orders array
      }

      // Fetch menu items with error handling
      try {
        const menuQuery = query(collection(db, 'food_items'));
        const menuSnapshot = await getDocs(menuQuery);
        menuItems = menuSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('🍽️ Fetched menu items:', menuItems.length);
      } catch (menuError) {
        console.warn('⚠️ Could not fetch menu items:', menuError.message);
        // Continue with empty menu items array
      }

      // Fetch discounts with error handling
      try {
        const discountsQuery = query(collection(db, 'discounts'), where('isActive', '==', true));
        const discountsSnapshot = await getDocs(discountsQuery);
        discounts = discountsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('💰 Fetched discounts:', discounts.length);
      } catch (discountsError) {
        console.warn('⚠️ Could not fetch discounts:', discountsError.message);
        // Continue with empty discounts array
      }

      // Calculate stats
      const totalOrders = orders.length;
      const pendingOrders = orders.filter(order => order.status === 'pending').length;
      const completedOrders = orders.filter(order => order.status === 'delivered').length;
      const totalRevenue = orders.reduce((sum, order) => sum + (order.total || 0), 0);
      const totalMenuItems = menuItems.length;
      const activeDiscounts = discounts.length;

      setStats({
        totalOrders,
        pendingOrders,
        completedOrders,
        totalRevenue,
        totalMenuItems,
        activeDiscounts
      });

      setRecentOrders(orders.slice(0, 5));
      
      console.log('✅ Dashboard data loaded successfully');
      
      // Only show error if ALL collections failed
      if (orders.length === 0 && menuItems.length === 0 && discounts.length === 0) {
        toast.error('No data available. Please add some content to see dashboard statistics.');
      }
    } catch (error) {
      console.error('❌ Critical error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      transition: 'all 0.3s ease'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          backgroundColor: `${color}20`,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 12px ${color}30`
        }}>
          <Icon style={{ width: '24px', height: '24px', color: color }} />
        </div>
        <div style={{ textAlign: 'right' }}>
          <h3 style={{
            fontSize: '28px',
            fontWeight: '800',
            color: '#1f2937',
            margin: '0 0 4px 0'
          }}>
            {value}
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: 0
          }}>
            {subtitle}
          </p>
        </div>
      </div>
      <h4 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
        margin: 0
      }}>
        {title}
      </h4>
    </div>
  );

  const QuickActionCard = ({ title, description, icon: Icon, color, onClick }) => (
    <div
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        textAlign: 'center'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
      }}
    >
      <div style={{
        width: '60px',
        height: '60px',
        backgroundColor: `${color}20`,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
        boxShadow: `0 4px 12px ${color}30`
      }}>
        <Icon style={{ width: '28px', height: '28px', color: color }} />
      </div>
      <h3 style={{
        fontSize: '18px',
        fontWeight: '700',
        color: '#1f2937',
        margin: '0 0 8px 0'
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '14px',
        color: '#6b7280',
        margin: 0,
        lineHeight: '1.4'
      }}>
        {description}
      </p>
    </div>
  );

  const RecentOrderCard = ({ order }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '16px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <div>
        <h4 style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#1f2937',
          margin: '0 0 4px 0'
        }}>
          Order #{order.orderId}
        </h4>
        <p style={{
          fontSize: '12px',
          color: '#6b7280',
          margin: '0 0 4px 0'
        }}>
          {order.deliveryAddress?.name || 'N/A'}
        </p>
        <p style={{
          fontSize: '12px',
          color: '#6b7280',
          margin: 0
        }}>
          ₹{order.total || 0}
        </p>
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        backgroundColor: `${getStatusColor(order.status)}20`,
        color: getStatusColor(order.status),
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '600'
      }}>
        {getStatusIcon(order.status)}
        <span style={{ textTransform: 'capitalize' }}>
          {order.status.replace('_', ' ')}
        </span>
      </div>
    </div>
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'confirmed': return '#3b82f6';
      case 'preparing': return '#8b5cf6';
      case 'ready': return '#10b981';
      case 'out_for_delivery': return '#f97316';
      case 'delivered': return '#059669';
      case 'cancelled': return '#dc2626';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock style={{ width: '12px', height: '12px' }} />;
      case 'confirmed': return <CheckCircle style={{ width: '12px', height: '12px' }} />;
      case 'preparing': return <Package style={{ width: '12px', height: '12px' }} />;
      case 'ready': return <CheckCircle style={{ width: '12px', height: '12px' }} />;
      case 'out_for_delivery': return <Users style={{ width: '12px', height: '12px' }} />;
      case 'delivered': return <CheckCircle style={{ width: '12px', height: '12px' }} />;
      case 'cancelled': return <XCircle style={{ width: '12px', height: '12px' }} />;
      default: return <Clock style={{ width: '12px', height: '12px' }} />;
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #ff6b35',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ fontSize: '16px', color: '#6b7280' }}>Loading dashboard...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        padding: '16px 0',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => navigate('/')}
                style={{
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowLeft style={{ width: '20px', height: '20px' }} />
              </button>
              <div style={{ width: '48px', height: '48px', background: 'linear-gradient(45deg, #ff6b35, #ff3f3f)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(255,107,53,0.3)' }}>
                <span style={{ color: 'white', fontWeight: '900', fontSize: '1.5rem' }}>B</span>
              </div>
              <div>
                <h1 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1f2937', margin: 0 }}>Admin Dashboard</h1>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>Manage your restaurant</p>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button
                onClick={fetchDashboardData}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <RefreshCw style={{ width: '16px', height: '16px' }} />
                Refresh
              </Button>
              <Button
                onClick={handleLogout}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <LogOut style={{ width: '16px', height: '16px' }} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Welcome Section */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '32px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '800',
            color: '#1f2937',
            margin: '0 0 8px 0'
          }}>
            Welcome to Admin Panel! 👋
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: '#6b7280',
            margin: '0 0 16px 0'
          }}>
            Manage your B.Tech Bawarchi restaurant efficiently
          </p>
          {stats.totalMenuItems === 0 && (
            <div style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '12px',
              padding: '16px',
              marginTop: '16px'
            }}>
              <p style={{
                fontSize: '14px',
                color: '#92400e',
                margin: 0,
                fontWeight: '600'
              }}>
                💡 Tip: Start by adding some food items to your menu to see dashboard statistics!
              </p>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <StatCard
            title="Total Orders"
            value={stats.totalOrders}
            icon={BarChart3}
            color="#3b82f6"
            subtitle="All time"
          />
          <StatCard
            title="Pending Orders"
            value={stats.pendingOrders}
            icon={Clock}
            color="#f59e0b"
            subtitle="Need attention"
          />
          <StatCard
            title="Completed Orders"
            value={stats.completedOrders}
            icon={CheckCircle}
            color="#10b981"
            subtitle="Delivered"
          />
          <StatCard
            title="Total Revenue"
            value={`₹${stats.totalRevenue.toLocaleString()}`}
            icon={DollarSign}
            color="#059669"
            subtitle="All time"
          />
          <StatCard
            title="Menu Items"
            value={stats.totalMenuItems}
            icon={Package}
            color="#ff6b35"
            subtitle="Available"
          />
          <StatCard
            title="Active Discounts"
            value={stats.activeDiscounts}
            icon={Percent}
            color="#8b5cf6"
            subtitle="Running"
          />
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '32px'
        }}>
          {/* Quick Actions */}
          <div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1f2937',
              margin: '0 0 20px 0'
            }}>
              Quick Actions
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px'
            }}>
              <QuickActionCard
                title="Menu Management"
                description="Add, edit, and remove dishes"
                icon={Package}
                color="#ff6b35"
                onClick={() => navigate('/admin/menu')}
              />
              <QuickActionCard
                title="Orders Management"
                description="View and manage orders"
                icon={Users}
                color="#3b82f6"
                onClick={() => navigate('/admin/orders')}
              />
              <QuickActionCard
                title="Promotions Management"
                description="Manage discounts & coupons"
                icon={Percent}
                color="#10b981"
                onClick={() => navigate('/admin/promotions')}
              />
            </div>
          </div>

          {/* Recent Orders */}
          <div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1f2937',
              margin: '0 0 20px 0'
            }}>
              Recent Orders
            </h3>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              {recentOrders.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    fontSize: '24px'
                  }}>
                    📦
                  </div>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 8px 0'
                  }}>
                    No orders yet
                  </h4>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: 0
                  }}>
                    Orders will appear here
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {recentOrders.map((order) => (
                    <RecentOrderCard key={order.id} order={order} />
                  ))}
                  <Button
                    onClick={() => navigate('/admin/orders')}
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginTop: '8px'
                    }}
                  >
                    View All Orders
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardNew;
